import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Star, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { CrudManager } from "@/components/admin/crud-manager";
import { adminUpdate } from "@/lib/admin";
import { azError } from "@/lib/az-errors";

export const Route = createFileRoute("/_authenticated/admin/reyler")({
  component: Page,
});

const statusLabels: Record<string, string> = {
  pending: "Gözləyir",
  approved: "Təsdiqlənib",
  rejected: "Rədd edilib",
};

function Page() {
  const qc = useQueryClient();
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      adminUpdate("reviews", id, { status }),
    onSuccess: () => {
      toast.success("Rəy statusu yeniləndi");
      qc.invalidateQueries({ queryKey: ["admin", "reviews"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader title="Rəylər" description="İstifadəçi rəylərini təsdiqlə və ya sil" />
      <CrudManager
        table="reviews"
        addLabel="Yeni rəy"
        emptyText="Rəy yoxdur."
        fields={[
          { name: "author_name", label: "Müəllif" },
          { name: "listing_id", label: "Elan ID" },
          { name: "rating", label: "Ulduz (1-5)", type: "number" },
          { name: "comment", label: "Rəy mətni", type: "textarea" },
          {
            name: "status",
            label: "Status",
            type: "select",
            options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          },
        ]}
        columns={[
          { name: "author_name", label: "Müəllif" },
          {
            name: "rating",
            label: "Ulduz",
            render: (r) => (
              <span className="inline-flex items-center gap-1">
                <Star className="size-3.5 fill-current text-amber-500" />
                {r["rating"]}
              </span>
            ),
          },
          { name: "comment", label: "Rəy" },
          {
            name: "status",
            label: "Status",
            render: (r) => (
              <Badge
                className="rounded-full"
                variant={r["status"] === "approved" ? "secondary" : r["status"] === "rejected" ? "destructive" : "outline"}
              >
                {statusLabels[r["status"]] ?? r["status"]}
              </Badge>
            ),
          },
        ]}
        extraActions={(r) => (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setStatus.mutate({ id: String(r["id"]), status: "approved" })}
            >
              <Check className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setStatus.mutate({ id: String(r["id"]), status: "rejected" })}
            >
              <X className="size-4" />
            </Button>
          </>
        )}
      />
    </div>
  );
}
